import {
  FormInput,
  spreadAndtransformDefaultValues,
  FormSelect, ISelectOption
} from "@common/components/forms";
import { useApp, useUnsavedChangesWarning } from "@common/hooks";
import { toTitleCase } from "@common/utils/formatting";
import { MedicalHistoryModel, PetDiagnosisModel } from "@common/models";
import { YesNoConfirmModal } from "@common/components/modals";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Col, Row } from "react-bootstrap";
import { useForm, useWatch } from "react-hook-form";
import { DateTime } from "luxon";
import { useHospitalApp } from "@hospital/hooks";
import { StringUtility, sortByInactiveStatusAndLastName } from "@common/utils";
import { useCreateLocationStore, useCreateUserStore } from "@hospital/stores";
import { DiagnosisStatusPill } from "@common/components/pills/DiagnosisStatusPill";
interface PetDiagnosisFormModel extends PetDiagnosisModel {
  veterinarianId?: number | null;
  firstDiagnosedBy?: string;
}

interface IExamFormUpdateDiagnosisStatusModalProps {
  visible: boolean;
  currentPetDiagnosis: PetDiagnosisModel;
  updatePetDiagnosis: (updatedDiagnosis: PetDiagnosisModel) => void;
  onClose: () => void;
  promiseTracker?: boolean;
  readOnly?: boolean;
  allowDateDiagnosedEdit?: boolean; //  Only show unsaved changes modal if editing of date is NOT allowed i.e in case of pet profile section
  isProfileUpdate?: boolean;
  firstDiagnosedBy?: string;
  size?: "xl" | "lg";
  diagnosisHistory?: MedicalHistoryModel[];
}

export const ExamFormUpdateDiagnosisStatusModal = observer(
  ({
    currentPetDiagnosis,
    updatePetDiagnosis,
    visible,
    onClose,
    promiseTracker,
    readOnly = false,
    allowDateDiagnosedEdit = false,
    isProfileUpdate,
    firstDiagnosedBy,
    size = "xl",
    diagnosisHistory
  }: IExamFormUpdateDiagnosisStatusModalProps) => {
    const { configuration } = useApp();
    const [showCancelConfirm, setShowCancelConfirm] = React.useState(false);
    const hospitalApp = useHospitalApp();
    const locationStore = useCreateLocationStore();
    const userStore = useCreateUserStore(configuration);

    const [currentVetId, setCurrentVetId] = useState<number>();
    // Initialize the form
    const methods = useForm<PetDiagnosisFormModel>({
      defaultValues: {
        ...spreadAndtransformDefaultValues(currentPetDiagnosis),
        firstDiagnosedBy: firstDiagnosedBy ?? "",
        latestPetDiagnosisStatus: {
          ...currentPetDiagnosis.latestPetDiagnosisStatus,
          veterinarianId: null,
          dateUpdated: DateTime.utc().setZone(hospitalApp.currentHospital?.store?.timezoneName).toISODate() ?? ""
        }
      }
    });

    const {
      control, watch,
      formState: { isDirty },
    } = methods;
    const dateCreatedValue = useWatch({
      control,
      name: "dateDiagnosed"
    });

    const selectedVetId = watch("veterinarianId");
    const [hospitalVets, setHospitalVets] = React.useState<ISelectOption[]>([])

    useEffect(() => {
      if (visible) {
        userStore.getUserImpl(configuration.userId);
      }
    }, [configuration.userId, visible, userStore])

    const selectedVet = useMemo(() => {
      if (!locationStore.hospitalVeterinarians) return undefined;

      return locationStore.hospitalVeterinarians.find(
        (v) => v.veterinarianId === selectedVetId
      );
    }, [locationStore.hospitalVeterinarians, selectedVetId]);

    const currentUserIsAttendingVet = useMemo(() => {
      return configuration.IsDVM() && userStore.vet !== undefined && selectedVet?.veterinarianId == userStore.vet.veterinarianId;
    }, [configuration, selectedVet, userStore.vet]);

    useEffect(() => {
      if (selectedVetId === undefined || selectedVetId == 0) {
        setCurrentVetId(userStore.vet.veterinarianId);
      }
    }, [currentUserIsAttendingVet, hospitalVets, selectedVetId, userStore?.vet?.veterinarianId])


    useEffect(() => {
      if (visible) {
        locationStore.loadHospitalhospitalVeterinarians(hospitalApp.currentHospital!.hospitalId);
      }
    }, [locationStore, hospitalApp.currentHospital, visible])

    useEffect(() => {
      if (selectedVetId) {
        methods.trigger("veterinarianId");
      }
    }, [selectedVetId, methods]);

    useEffect(() => {
      const veterinarians = locationStore.hospitalVeterinarians;
      if (veterinarians) {
        const sortedVeterinarians = sortByInactiveStatusAndLastName(veterinarians);
        const vetSelectOptions = sortedVeterinarians.map(({ firstName, lastName, title, veterinarianId, employmentStatus }) => ({ label: StringUtility.formattedUserNameWithInactive(firstName, lastName, title, employmentStatus), value: veterinarianId }))
        setHospitalVets(vetSelectOptions);
      }
    }, [locationStore.hospitalVeterinarians, hospitalApp.currentHospital, hospitalApp.currentHospital?.store?.stateId])


    useEffect(() => {
      if (!hospitalVets.length) return;

      const currentValue = methods.getValues("veterinarianId");

      if (currentVetId) {
        methods.setValue("veterinarianId", currentVetId, {
          shouldDirty: false
        });
        return;
      }

      if (!currentValue && userStore?.vet?.veterinarianId) {
        const isVetInHospital = hospitalVets.some(
          v => v.value === userStore.vet.veterinarianId
        );

        if (isVetInHospital) {
          methods.setValue("veterinarianId", userStore.vet.veterinarianId, {
            shouldDirty: false
          });
        }
      }
    }, [hospitalVets, currentVetId, userStore?.vet?.veterinarianId, methods]);



    const unsavedChangesPrompt = useUnsavedChangesWarning(isDirty);
    const diagnosisStatusOptions = configuration.diagnosisStatuses.map(
      (ds) => ({
        value: ds.diagnosisStatusId,
        label: ds.name,
      }),
    );

    const PetDiagnosisStatusHistory = ({
      diagnosisHistory
    }: {
      diagnosisHistory: MedicalHistoryModel[] | undefined;
    }) => {

      return (
        <div className="table-responsive">
          <table className="table w-100">

            <thead>
              <tr className="pet-medical-history-table-headers">
                <th className="p-0 ps-2 pe-3 text-nowrap w-auto">Date</th>
                <th className="text-nowrap px-3 w-auto">Provider</th>
                <th className="text-nowrap px-3 w-auto">Status</th>
                <th className="px-3 w-100">Notes</th>
              </tr>
            </thead>
            <tbody>
              {diagnosisHistory?.map((a, idx) => {
                const idBase = a.petDiagnosisStatusHistoryId ?? idx;
                return (
                  <tr key={idBase} className="pet-medical-history-row">
                    <td className="p-0 ps-2 pe-3 text-nowrap">
                      <span>{DateTime.fromISO(a.salesDate, { zone: "utc" }).setZone(hospitalApp.currentHospital?.store?.timezoneName).toLocaleString(DateTime.DATE_SHORT)}</span>
                    </td>
                    <td className="px-3 text-nowrap">
                      <span>{a.petDiagnosisStatusChangedBy}</span>
                    </td>
                    <td className="px-3 text-nowrap th-status">
                      <span>
                        <DiagnosisStatusPill
                          diagnosisStatusId={a.diagnosisStatusId || 0}
                        ></DiagnosisStatusPill>
                      </span>
                    </td>
                    <td className="px-3 w-100 text-break">
                      <span
                        id={`attr-val-${idBase}`}
                        data-testid={`attr-val-${idBase}`}
                      >
                        {a.petDiagnosisStatusHistoryNote}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    };

    //effect to handle utc to hospital time zone conversion
    useEffect(() => {
      if (!visible) return;
    
      let formattedDate = currentPetDiagnosis.dateDiagnosed;

      //Only convert if it's a full ISO datetime (has time)
      if (formattedDate.includes("T")) {
        formattedDate = DateTime
          .fromISO(formattedDate, { zone: "utc" })
          .setZone(hospitalApp.currentHospital?.store?.timezoneName)
          .toISO(); 
      }
      methods.setValue("dateDiagnosed", formattedDate, {
        shouldDirty: false,
      });
    }, [visible, currentPetDiagnosis.dateDiagnosed, hospitalApp.currentHospital, methods]);

    // Confirm before closing if there are unsaved changes
    const checkConfirmDirty = () => {
      if (!allowDateDiagnosedEdit && isDirty) setShowCancelConfirm(true);
      else onClose();
    };

    const saveChanges = async () => {
      const isValid = await methods.trigger();
      if (!isValid) return;
      const updatedData: PetDiagnosisModel = methods.getValues();
      updatedData.latestPetDiagnosisStatus.veterinarianId = selectedVetId ?? 0;
      updatedData.latestPetDiagnosisStatus.storeId = hospitalApp.currentHospital?.store?.storeId ?? null;
      updatedData.latestPetDiagnosisStatus.changedByUserName = hospitalVets?.find(vet => vet.value === updatedData.latestPetDiagnosisStatus.veterinarianId)?.label ?? "";

      updatePetDiagnosis(updatedData);

    };


    return (
      <>
        {unsavedChangesPrompt}

        {/* Cancel confirmation */}
        <YesNoConfirmModal
          visible={showCancelConfirm}
          title="Cancel Changes"
          description="You have unsaved changes, are you sure you want to cancel?"
          busy={!!promiseTracker}
          cancelButtonText="No"
          onConfirm={onClose}
          onCancel={() => setShowCancelConfirm(false)}
        />

        {/* Main modal */}
        <Modal
          size={size}
          centered
          show={visible}
          backdrop="static"
          onHide={checkConfirmDirty}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {`Update ${toTitleCase(currentPetDiagnosis.diagnosis.name)} Diagnosis`}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="mt-3">

              {isProfileUpdate ? (
                <>
                  {/* Layout with Attending Doctor */}

                  <Row className="mb-3">
                    <Col>
                      <FormInput
                        type="text"
                        label="Diagnosis"
                        fieldName="diagnosis.name"
                        className="ms-0"
                        control={control}
                        disabled
                      />
                    </Col>

                    <Col>
                      <FormInput
                        type="date"
                        label="Date Diagnosed"
                        fieldName="dateDiagnosed"
                        className="ms-0"
                        control={control}
                        isClearable={false}
                        disabled={!allowDateDiagnosedEdit || readOnly}
                        maxDate={new Date()}
                        portalId="root-portal"
                      />
                    </Col>

                    <Col>
                      <FormInput
                        type="text"
                        label="Diagnosed By"
                        fieldName="firstDiagnosedBy"
                        className="ms-0"
                        control={control}
                        disabled
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col>
                      <div style={{ width: "22rem" }}>
                        <FormSelect
                          control={control}
                          fieldName="veterinarianId"
                          label="Updated By"
                          options={hospitalVets}
                          highlightDirty
                          mapOptionToValue={opt => opt?.value}
                          mapValueToOption={value => _.find(hospitalVets, { value }) }
                          isRequired
                          isDisabled={readOnly}
                        />
                      </div>
                    </Col>

                    <Col>
                      <FormInput
                        type="date"
                        label="Date Updated"
                        fieldName="latestPetDiagnosisStatus.dateUpdated"
                        className="ms-0"
                        control={control}
                        isClearable={false}
                        maxDate={new Date()}
                        portalId="root-portal"
                        disabled
                      />
                    </Col>

                    <Col>
                      <FormSelect
                        control={control}
                        fieldName="diagnosisStatusId"
                        label="Status"
                        options={diagnosisStatusOptions}
                        highlightDirty
                        isDisabled={readOnly}
                        mapOptionToValue={(opt) => opt?.value}
                        mapValueToOption={(value) =>
                          _.find(diagnosisStatusOptions, { value })
                        }
                        isRequired
                      />
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  {/* Old Layout with out attending doctor */}

                  <Row className="mb-3">
                    <Col>
                      <FormInput
                        type="text"
                        label="Diagnosis"
                        fieldName="diagnosis.name"
                        className="ms-0"
                        control={control}
                        disabled
                      />
                    </Col>

                    <Col>
                      <FormInput
                        type="date"
                        label="Date Diagnosed"
                        fieldName="dateDiagnosed"
                        className="ms-0"
                        control={control}
                        isClearable={false}
                        disabled={!allowDateDiagnosedEdit || readOnly}
                        maxDate={new Date()}
                        portalId="root-portal"
                      />
                    </Col>

                    <Col>
                      <FormSelect
                        control={control}
                        fieldName="diagnosisStatusId"
                        label="Status"
                        options={diagnosisStatusOptions}
                        highlightDirty
                        isDisabled={readOnly}
                        mapOptionToValue={(opt) => opt?.value}
                        mapValueToOption={(value) =>
                          _.find(diagnosisStatusOptions, { value })
                        }
                        isRequired
                      />
                    </Col>
                  </Row>
                </>
              )}

              <Row className="mt-5">
                <Col>
                  <FormInput
                    rows={3}
                    type="textarea"
                    label="Notes"
                    fieldName="latestPetDiagnosisStatus.notes"
                    className="ms-0"
                    control={control}
                    disabled={readOnly}
                    rules={{
                      maxLength: {
                        value: 1000,
                        message: "Maximum length is 1000 characters",
                      },
                    }}
                  />
                </Col>
              </Row>
              {isProfileUpdate ?
                <Row>
                  <PetDiagnosisStatusHistory diagnosisHistory={diagnosisHistory} />
                </Row> : null
              }

            </div>
          </Modal.Body>

          <Modal.Footer className="container-fluid">
            <div className="d-flex-row">
              <Button
                variant="light"
                className="cancel text-uppercase"
                onClick={checkConfirmDirty}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="confirm text-uppercase"
                onClick={saveChanges}
                disabled={readOnly || !dateCreatedValue}
              >
                Save
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      </>
    );
  },
);
